import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import PageMockups from "@/components/PageMockups";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, FileText, ArrowLeft, Database, Layout, Code, Rocket, Users, Palette, DollarSign, Zap } from "lucide-react";

function Section({ icon: Icon, title, children, color = "indigo" }) {
  return (
    <div className="glass rounded-2xl p-6 card-hover" data-testid={`section-${title}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-lg bg-${color}-500/10 border border-${color}-500/20 flex items-center justify-center`}>
          <Icon size={16} className={`text-${color}-400`} />
        </div>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
      } catch {
        toast.error("Projet introuvable");
        navigate("/tableau-de-bord");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const download = () => {
    if (!project) return;
    const blob = new Blob([JSON.stringify(project.blueprint, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(project.blueprint.name || "saas").toLowerCase().replace(/\s+/g, "-")}-blueprint.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Blueprint téléchargé !");
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    if (!project) return;
    setPdfLoading(true);
    try {
      const { data } = await api.get(`/projects/${id}/pitch-deck`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project.blueprint.name || "saas").toLowerCase().replace(/\s+/g, "-")}-pitch-deck.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pitch deck PDF téléchargé !");
    } catch {
      toast.error("Impossible de générer le pitch deck");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center pt-40"><Loader2 className="animate-spin text-gray-400" /></div>
      </div>
    );
  }
  if (!project) return null;
  const bp = project.blueprint || {};
  const palette = bp.color_palette || {};

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="project-detail-main">
        <Link to="/tableau-de-bord" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        {/* Hero */}
        <div className="mt-6 rounded-3xl p-8 sm:p-12 relative overflow-hidden animate-fade-up" style={{ background: `linear-gradient(135deg, ${palette.primary || '#6366F1'}20, ${palette.accent || '#06B6D4'}10)`, border: `1px solid ${palette.primary || '#6366F1'}30` }} data-testid="project-hero">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <div className="text-xs font-mono uppercase tracking-widest text-gray-300">Blueprint généré</div>
              <h1 className="text-3xl sm:text-5xl font-extrabold mt-2" style={{ color: palette.primary || '#F9FAFB' }} data-testid="project-name">
                {bp.name}
              </h1>
              <p className="mt-4 text-lg text-gray-200 max-w-2xl">{bp.tagline}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={downloadPdf} disabled={pdfLoading} className="pill-primary flex items-center gap-2 disabled:opacity-60" data-testid="download-pitch-deck-button">
                  {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Télécharger le pitch deck PDF
                </button>
                <button onClick={download} className="pill-ghost flex items-center gap-2" data-testid="download-blueprint-button">
                  <Download size={16} /> Blueprint JSON
                </button>
              </div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-gray-400">Palette</div>
              <div className="mt-3 flex gap-2">
                {[palette.primary, palette.secondary, palette.accent].filter(Boolean).map((c, i) => (
                  <div key={i} className="flex-1">
                    <div className="h-12 rounded-lg border border-white/10" style={{ background: c }} />
                    <div className="mt-2 text-[10px] font-mono text-gray-400">{c}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <PageMockups projectId={id} pages={bp.pages || []} />

        {/* Grid */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <Section icon={Users} title="Public cible" color="indigo">
            <p className="text-gray-300 text-sm leading-relaxed">{bp.target_audience}</p>
          </Section>
          <Section icon={DollarSign} title="Modèle économique" color="emerald">
            <p className="text-gray-300 text-sm leading-relaxed">{bp.monetization}</p>
          </Section>
        </div>

        <div className="mt-6">
          <Section icon={Zap} title="Proposition de valeur" color="amber">
            <p className="text-gray-200 text-lg leading-relaxed">{bp.value_proposition}</p>
          </Section>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <Section icon={Palette} title="Fonctionnalités clés" color="cyan">
            <ul className="space-y-3">
              {(bp.core_features || []).map((f, i) => (
                <li key={i} className="border-l-2 border-cyan-500/40 pl-3">
                  <div className="text-sm font-semibold">{f.name}</div>
                  <div className="text-xs text-gray-400 mt-1">{f.description}</div>
                </li>
              ))}
            </ul>
          </Section>
          <Section icon={Layout} title="Pages & Écrans" color="indigo">
            <ul className="space-y-3">
              {(bp.pages || []).map((p, i) => (
                <li key={i} className="border-l-2 border-indigo-500/40 pl-3">
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-xs text-gray-400 mt-1">{p.purpose}</div>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <Section icon={Database} title="Schéma de base de données" color="emerald">
            <div className="space-y-3">
              {(bp.database_schema || []).map((t, i) => (
                <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-sm font-mono font-semibold text-emerald-300">{t.table}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(t.fields || []).map((f, j) => (
                      <span key={j} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-gray-400">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
          <Section icon={Code} title="Endpoints API" color="amber">
            <div className="space-y-2 max-h-96 overflow-auto">
              {(bp.api_endpoints || []).map((e, i) => (
                <div key={i} className="flex items-start gap-3 text-xs font-mono">
                  <span className={`px-2 py-0.5 rounded font-semibold ${e.method === 'GET' ? 'bg-emerald-500/10 text-emerald-300' : e.method === 'POST' ? 'bg-indigo-500/10 text-indigo-300' : e.method === 'DELETE' ? 'bg-red-500/10 text-red-300' : 'bg-amber-500/10 text-amber-300'}`}>{e.method}</span>
                  <div className="flex-1">
                    <div className="text-gray-200">{e.path}</div>
                    <div className="text-gray-500 mt-0.5">{e.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <Section icon={Code} title="Stack technique" color="cyan">
            <div className="space-y-2 text-sm">
              {Object.entries(bp.tech_stack || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 pb-2">
                  <span className="capitalize text-gray-400 font-mono text-xs uppercase tracking-wider">{k}</span>
                  <span className="text-gray-200">{v}</span>
                </div>
              ))}
            </div>
          </Section>
          <Section icon={Rocket} title="Roadmap MVP" color="indigo">
            <ol className="space-y-3">
              {(bp.mvp_roadmap || []).map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-xs font-bold text-indigo-300 shrink-0">{i + 1}</span>
                  <span className="text-sm text-gray-300 pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </Section>
        </div>
      </main>
    </div>
  );
}
